import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, MessageCircle, ChevronDown, ChevronUp, Send, AlertCircle, FileSpreadsheet } from 'lucide-react';

const WhatsAppDispatch = () => {
  const { API } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [outstandings, setOutstandings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageType, setMessageType] = useState('hisaab');
  const [selectedUpload, setSelectedUpload] = useState('');
  const [messages, setMessages] = useState([]);
  const [building, setBuilding] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [driversRes, uploadsRes, osRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/drivers`),
        axios.get(`${API}/hisaab/uploads`),
        axios.get(`${API}/outstandings`),
        axios.get(`${API}/vendors`),
      ]);
      setDrivers(driversRes.data);
      setUploads(uploadsRes.data);
      setOutstandings(osRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const driverMobileMap = {};
  for (const d of drivers) {
    driverMobileMap[d.driver_id] = d.mobile;
    driverMobileMap[d.id] = d.mobile;
  }

  const buildMessages = async () => {
    setBuilding(true);
    setMessages([]);
    try {
      if (messageType === 'hisaab') {
        if (!selectedUpload) { toast.error('Select a hisaab upload'); setBuilding(false); return; }
        const res = await axios.get(`${API}/hisaab/records`, { params: { upload_id: selectedUpload } });
        const records = res.data;
        const grouped = {};
        for (const r of records) {
          const key = r.driver_id_ext || r.name;
          if (!grouped[key]) grouped[key] = { name: r.name, driver_id_ext: r.driver_id_ext, entries: [] };
          grouped[key].entries.push(r);
        }
        const msgs = Object.values(grouped).map(g => {
          const mobile = driverMobileMap[g.driver_id_ext] || '';
          let text = `*Hisaab - ${g.name} (${g.driver_id_ext})*\n\n`;
          let totalOS = 0;
          for (const e of g.entries) {
            text += `Vehicle: ${e.vehicle_number}\n`;
            text += `Earnings: ${e.your_earnings}, Trips: ${e.trips}\n`;
            text += `Lease: ${e.total_lease}, Final OS: ${e.final_os}\n\n`;
            totalOS += e.final_os;
          }
          text += `*Total Outstanding: ${totalOS.toFixed(2)}*`;
          const waLink = mobile ? `https://wa.me/91${mobile.replace(/\D/g, '')}?text=${encodeURIComponent(text)}` : '';
          return { name: g.name, id: g.driver_id_ext, mobile, vehicleCount: g.entries.length, text, waLink, totalOS };
        });
        setMessages(msgs);
      } else if (messageType === 'outstanding') {
        const msgs = outstandings.filter(o => o.closing_os !== 0).map(o => {
          const mobile = driverMobileMap[o.driver_id_ext] || '';
          const text = `*Outstanding Reminder*\n\nDear ${o.driver_name},\n\nYour current outstanding: *${o.closing_os.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}*\n\nOpening: ${o.opening_os}\nHisaab: ${o.hisaab_amount}\nReceived: ${o.received}\nPaid: ${o.paid}\nClosing: ${o.closing_os}\n\nPlease clear at earliest.\n- National Mobility`;
          const waLink = mobile ? `https://wa.me/91${mobile.replace(/\D/g, '')}?text=${encodeURIComponent(text)}` : '';
          return { name: o.driver_name, id: o.driver_id_ext, mobile, text, waLink, totalOS: o.closing_os };
        });
        setMessages(msgs);
      } else if (messageType === 'vendor') {
        const msgs = [];
        for (const v of vendors) {
          if (!v.mobile) continue;
          try {
            const res = await axios.get(`${API}/vendors/ledger/${v.id}`);
            const ld = res.data;
            let text = `*Vendor Statement - ${v.name}*\n\nTotal Paid: ${ld.total_paid.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}\nTotal Received: ${ld.total_received.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}\nBalance: ${ld.balance.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}\n\n- National Mobility`;
            const waLink = `https://wa.me/91${v.mobile.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;
            msgs.push({ name: v.name, id: v.vendor_id, mobile: v.mobile, text, waLink, totalOS: ld.balance });
          } catch { /* skip */ }
        }
        setMessages(msgs);
      }
    } catch (error) {
      toast.error('Failed to build messages');
    } finally {
      setBuilding(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="whatsapp-section">
      {/* Controls */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">WhatsApp Dispatch</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1.5">
              <p className="text-xs text-slate-500 font-medium">Message Type</p>
              <Select value={messageType} onValueChange={setMessageType}>
                <SelectTrigger className="w-[200px]" data-testid="msg-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hisaab">Send Hisaab</SelectItem>
                  <SelectItem value="outstanding">Outstanding Reminder</SelectItem>
                  <SelectItem value="vendor">Vendor Statement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {messageType === 'hisaab' && (
              <div className="space-y-1.5">
                <p className="text-xs text-slate-500 font-medium">Select Upload</p>
                <Select value={selectedUpload} onValueChange={setSelectedUpload}>
                  <SelectTrigger className="w-[260px]" data-testid="upload-select">
                    <SelectValue placeholder="Select hisaab upload..." />
                  </SelectTrigger>
                  <SelectContent>
                    {uploads.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.filename} (Week: {u.week_begin || 'N/A'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={buildMessages} disabled={building} className="bg-green-600 text-white hover:bg-green-700" data-testid="build-messages-btn">
              {building ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
              Build Messages
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Messages List */}
      {messages.length > 0 && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">
              {messages.length} Messages Ready
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {messages.map((msg, idx) => (
              <div key={idx} className="border rounded-lg p-4 hover:border-slate-300 transition-colors" data-testid={`wa-msg-${idx}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{msg.name}</span>
                      <span className="font-mono text-xs text-slate-500">({msg.id})</span>
                      {msg.vehicleCount > 1 && (
                        <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                          {msg.vehicleCount} vehicles
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      Mobile: {msg.mobile || 'Not linked'} |
                      Amount: {(msg.totalOS || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                    </p>
                    <button className="text-xs text-blue-600 mt-1 flex items-center gap-1"
                      onClick={() => setExpanded(expanded === idx ? null : idx)}>
                      {expanded === idx ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {expanded === idx ? 'Hide' : 'Preview'}
                    </button>
                    {expanded === idx && (
                      <pre className="mt-2 text-xs bg-slate-50 p-3 rounded whitespace-pre-wrap border max-h-48 overflow-y-auto">{msg.text}</pre>
                    )}
                  </div>
                  {msg.waLink ? (
                    <a href={msg.waLink} target="_blank" rel="noopener noreferrer" data-testid={`wa-send-${idx}`}>
                      <Button size="sm" className="bg-green-600 text-white hover:bg-green-700">
                        <Send className="w-4 h-4 mr-1" /> Send
                      </Button>
                    </a>
                  ) : (
                    <Button size="sm" disabled variant="outline" className="text-slate-400">
                      <AlertCircle className="w-4 h-4 mr-1" /> No mobile
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {messages.length === 0 && !building && (
        <Card className="border border-dashed border-slate-300">
          <CardContent className="py-12 text-center text-slate-400">
            <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Select message type and click "Build Messages" to generate WhatsApp dispatches.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WhatsAppDispatch;
